import React from 'react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { X, User, Mail, CreditCard, BookOpen, Users, Calendar } from 'lucide-react';
import { getPhotoUrl } from '../lib/photoUtils';

interface SchuelerStammblattModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentUnid: string;
}

interface StudentStammblattData {
  ID: number;
  sophomorixUnid: string;
  Vorname: string;
  Name: string;
  Klasse: string;
  Email?: string;
  RFID?: string;
  Diff_D?: string;
  Diff_M?: string;
  Diff_E?: string;
  Memo?: string;
  Geburtsdatum?: string;
  courses?: Array<{ id: number; name: string; fach?: string; jahrgang?: string }>;
  groups?: Array<{ id: number; name: string; gruppen_typ?: string }>;
}

const formatDate = (d: string | null | undefined) => {
  if (!d) return '–';
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? d : date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return d;
  }
};

export default function SchuelerStammblattModal({
  isOpen,
  onClose,
  studentUnid
}: SchuelerStammblattModalProps) {
  const { data: student, isLoading } = useQuery<StudentStammblattData>(
    ['student-stammblatt', studentUnid],
    async () => {
      const response = await api.get(`/students/unid/${studentUnid}`);
      return response.data;
    },
    { enabled: isOpen && !!studentUnid }
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Schülerstammblatt</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : student ? (
            <div className="space-y-6">
              {/* Foto + Name */}
              <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-gray-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <User className="w-10 h-10 text-gray-400" />
                  </div>
                  <img
                    src={getPhotoUrl(studentUnid)}
                    alt={`${student.Vorname} ${student.Name}`}
                    className="w-full h-full object-cover relative z-10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {student.Vorname} {student.Name}
                  </h3>
                  <p className="text-gray-600">Klasse {student.Klasse}</p>
                </div>
              </div>

              {/* Stammdaten */}
              <div className="grid gap-3">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Stammdaten
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">Geburtsdatum:</span>
                    <span>{formatDate(student.Geburtsdatum)}</span>
                  </div>
                  {student.Email && (
                    <div className="flex items-center gap-2 sm:col-span-2">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600">E-Mail:</span>
                      <a href={`mailto:${student.Email}`} className="text-blue-600 hover:underline truncate">
                        {student.Email}
                      </a>
                    </div>
                  )}
                  {student.RFID && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600">RFID:</span>
                      <span className="font-mono text-xs">{student.RFID}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Fächer-Einstufungen */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4" />
                  Fächer-Einstufungen
                </h4>
                <div className="flex gap-4">
                  <div className="flex-1 bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-600">Deutsch</div>
                    <div className="text-lg font-bold text-blue-700">{student.Diff_D || '–'}</div>
                  </div>
                  <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-600">Mathematik</div>
                    <div className="text-lg font-bold text-green-700">{student.Diff_M || '–'}</div>
                  </div>
                  <div className="flex-1 bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-600">Englisch</div>
                    <div className="text-lg font-bold text-purple-700">{student.Diff_E || '–'}</div>
                  </div>
                </div>
              </div>

              {/* Memo */}
              {student.Memo && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Notizen</h4>
                  <div className="bg-amber-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {student.Memo}
                  </div>
                </div>
              )}

              {/* Kurse */}
              {student.courses && student.courses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4" />
                    Kurse
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {student.courses.map((c) => (
                      <span
                        key={c.id}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                      >
                        {c.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Gruppen */}
              {student.groups && student.groups.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    Gruppen
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {student.groups.map((g) => (
                      <span
                        key={g.id}
                        className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs"
                      >
                        {g.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Schülerdaten konnten nicht geladen werden.</p>
          )}
        </div>
      </div>
    </div>
  );
}
